import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useOcelMetadata } from "../../../stores/OcelContext";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import { HoverInfoCard, TooltipProvider } from "../../../components/common/tooltip/TooltipSurfaces";
import ObjectAttributeTable, { TOOLTIP_OBJECT_LIMIT } from "../../../components/common/ObjectAttributeTable";
import { createFilteredRequest, makePostRequest } from "../../../services/apiClient";
import EfficiencyKPISettings, { makeRowId } from "./EfficiencyKPISettings";
import { computeFormulaValue, collectFormulaAttributes, resolveAmpelColor } from "./formula";
import { formatAttributeValue } from "../../visualization/GanttChart/ganttTransform";
import { formatBerlinDisplay } from "../../../utils/berlinDate";
import {
  ALL_TIME_PRESET,
  resolveCurrentRange,
  resolvePreviousRange,
  formatRangeLabel,
  formatComparisonCaption,
  detectPeriodPresetFromDateRange,
} from "../_shared/periodPresets";
import {
  TREND_BADGE_CLASSES,
  KPIValueRow,
  KPIValueColumn,
  KPIValueSlot,
  KPITrendSlot,
  KPIBigValue,
  KPICaption,
  KPITrendBadge,
} from "../_shared/kpiTrendDisplay";

const defaultRow = () => [{ id: makeRowId(), attribute: "", operator: null }];

const ERROR_MESSAGES = {
  MISSING_DATA: "Daten nicht verfügbar",
  DENOMINATOR_ZERO: "Nenner ist 0 — Berechnung nicht möglich",
};

const formatPercent = (value) =>
  `${value.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

// Trend indicator is absolute percentage points (aktueller Wert - Vorperiodenwert),
// not a relative % change like Aggregation KPI — both formulaResult values are
// already 0-100 percentages, so the raw difference already is "pp".
const formatTrendPP = (trend) => {
  const sign = trend.diff > 0 ? "+" : trend.diff < 0 ? "−" : "";
  return `${sign}${Math.abs(trend.diff).toFixed(1)} pp`;
};

const EfficiencyKPI = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    globalFilterState,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const metadata = useOcelMetadata(activeFile);

  // Component configuration
  const [componentTitle, setComponentTitle] = useState("");
  const [objectType, setObjectType] = useState("");
  const [numerator, setNumerator] = useState(defaultRow);
  const [denominator, setDenominator] = useState(defaultRow);
  const [tooltipAttributes, setTooltipAttributes] = useState([]);
  const [ampelEnabled, setAmpelEnabled] = useState(false);
  const [ampelLower, setAmpelLower] = useState(50);
  const [ampelUpper, setAmpelUpper] = useState(80);
  const [ampelColorRed, setAmpelColorRed] = useState("#ef4444");
  const [ampelColorYellow, setAmpelColorYellow] = useState("#eab308");
  const [ampelColorGreen, setAmpelColorGreen] = useState("#22c55e");

  // Betrachtungszeitraum / Vorperioden-Vergleich configuration
  const [periodPreset, setPeriodPreset] = useState(ALL_TIME_PRESET.key);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showPeriodLabel, setShowPeriodLabel] = useState(true);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [trendDirection, setTrendDirection] = useState("higherIsBetter");

  // Data state
  const [kpiData, setKpiData] = useState(null);
  const [previousKpiData, setPreviousKpiData] = useState(null);
  const [resolvedRange, setResolvedRange] = useState(null);
  const [resolvedPreviousRange, setResolvedPreviousRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);

  // Modal
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Derived metadata — availableObjectTypes filtered by global object-type filter only
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);

  useEffect(() => {
    if (!metadata?.object_types) return;
    const allTypes = Object.keys(metadata.object_types).sort();
    const filtered =
      globalFilterState?.objectTypes?.length > 0
        ? allTypes.filter((t) => globalFilterState.objectTypes.includes(t))
        : allTypes;
    setAvailableObjectTypes(filtered);
    if (objectType && !filtered.includes(objectType)) {
      setObjectType(filtered[0] ?? "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterState?.objectTypes, metadata]);

  const availableActivities = useMemo(
    () => (metadata?.activity_types ? Object.keys(metadata.activity_types).sort() : []),
    [metadata]
  );

  // Unit + type lookup — straight from the OCEL schema already on metadata,
  // scoped to the selected object type (mirrors AggregationKPI.jsx). Feeds
  // the tooltip object table's per-cell formatting (timestamp vs numeric vs
  // plain string).
  const attributeUnitMap = useMemo(() => {
    const map = {};
    const schemas = objectType
      ? (metadata?.object_types_schema ?? []).filter((t) => t.name === objectType)
      : (metadata?.object_types_schema ?? []);
    for (const t of schemas) {
      for (const attr of (t.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit;
      }
    }
    return map;
  }, [metadata, objectType]);

  const attributeTypeMap = useMemo(() => {
    const map = {};
    const schemas = objectType
      ? (metadata?.object_types_schema ?? []).filter((t) => t.name === objectType)
      : (metadata?.object_types_schema ?? []);
    for (const t of schemas) {
      for (const attr of (t.attributes ?? [])) {
        if (attr.type) map[attr.name] = attr.type;
      }
    }
    return map;
  }, [metadata, objectType]);

  // Tooltip object table cell formatting — missing values render as an empty
  // string (cell still shown, per spec), timestamps use the same Berlin
  // display format used elsewhere in the app, everything else reuses Gantt's
  // shared numeric/unit formatter instead of reinventing it here.
  const formatCellValue = useCallback((attr, value) => {
    if (value == null) return "";
    const type = attributeTypeMap[attr];
    if (type === "timestamp" || type === "date") {
      return formatBerlinDisplay(value) ?? "";
    }
    const isNumericAttr = type === "float" || type === "integer" || type === "double" || type === "number";
    return formatAttributeValue(value, {
      hasDecimals: true,
      unit: attributeUnitMap[attr],
      isNumericAttr,
    });
  }, [attributeTypeMap, attributeUnitMap]);

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.numerator?.length) setNumerator(initialSettings.numerator);
    if (initialSettings.denominator?.length) setDenominator(initialSettings.denominator);
    if (initialSettings.tooltipAttributes !== undefined) setTooltipAttributes(initialSettings.tooltipAttributes);
    if (initialSettings.ampelEnabled !== undefined) setAmpelEnabled(initialSettings.ampelEnabled);
    if (initialSettings.ampelLower !== undefined) setAmpelLower(initialSettings.ampelLower);
    if (initialSettings.ampelUpper !== undefined) setAmpelUpper(initialSettings.ampelUpper);
    if (initialSettings.ampelColorRed !== undefined) setAmpelColorRed(initialSettings.ampelColorRed);
    if (initialSettings.ampelColorYellow !== undefined) setAmpelColorYellow(initialSettings.ampelColorYellow);
    if (initialSettings.ampelColorGreen !== undefined) setAmpelColorGreen(initialSettings.ampelColorGreen);
    if (initialSettings.periodPreset !== undefined) {
      setPeriodPreset(initialSettings.periodPreset);
    } else {
      // Configs saved before periodPreset existed — derive a best-guess preset
      // from the legacy resolved dateRange so old tiles keep working unchanged.
      setPeriodPreset(detectPeriodPresetFromDateRange(initialSettings.localFilters?.dateRange));
    }
    if (initialSettings.customStart !== undefined) setCustomStart(initialSettings.customStart);
    if (initialSettings.customEnd !== undefined) setCustomEnd(initialSettings.customEnd);
    if (initialSettings.showPeriodLabel !== undefined) setShowPeriodLabel(initialSettings.showPeriodLabel);
    if (initialSettings.comparisonEnabled !== undefined) setComparisonEnabled(initialSettings.comparisonEnabled);
    if (initialSettings.trendDirection !== undefined) setTrendDirection(initialSettings.trendDirection);
    setPendingApply(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      componentTitle,
      objectType,
      numerator,
      denominator,
      tooltipAttributes,
      ampelEnabled,
      ampelLower,
      ampelUpper,
      ampelColorRed,
      ampelColorYellow,
      ampelColorGreen,
      localFilters,
      periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
    }));
  }, [
    componentTitle, objectType, numerator, denominator, tooltipAttributes,
    ampelEnabled, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen,
    localFilters,
    periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Settings modal lifecycle ───────────────────────────────────────────────
  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback(
    (val) => {
      setIsSettingsModalOpen(val);
      if (!val) cancelDraftFilters();
    },
    [cancelDraftFilters]
  );

  // ── Data fetching ──────────────────────────────────────────────────────────
  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    const attributes = collectFormulaAttributes(numerator, denominator);
    if (attributes.length === 0 || !activeFile) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const now = new Date();
      const currentRange = resolveCurrentRange(periodPreset, { customStart, customEnd }, now);
      const previousRange = comparisonEnabled
        ? resolvePreviousRange(periodPreset, currentRange, now)
        : null;

      // A non-"all" period is itself a local override, same as any other
      // local filter — decide local-vs-global the same way useLocalFilters
      // does, but without depending on this render's (possibly stale) value.
      const usesLocalFilters = hasLocalFilters || periodPreset !== ALL_TIME_PRESET.key;
      const filterBase = usesLocalFilters ? localFilters : globalFilterState;

      // Tooltip object table (see Tooltip A below) rides the same request that
      // fetches the formula's numeric sums — only the current-period request
      // needs the extra columns/rows, since the previous-period value never
      // renders a table. wantsObjectRows uses the raw configured list; dedup
      // against the formula's own attributes only applies to the request
      // payload, not to whether rows are fetched at all.
      const wantsObjectRows = tooltipAttributes.length > 0;
      const extraAttrs = tooltipAttributes.filter((a) => !attributes.includes(a));

      const currentBaseRequest = {
        file_type: activeFile,
        object_type: objectType || undefined,
        selected_attributes: wantsObjectRows ? [...attributes, ...extraAttrs] : attributes,
        limit: wantsObjectRows ? TOOLTIP_OBJECT_LIMIT : 1,
        offset: 0,
      };
      const previousBaseRequest = {
        file_type: activeFile,
        object_type: objectType || undefined,
        selected_attributes: attributes,
        limit: 1,
        offset: 0,
      };

      const currentRequest = createFilteredRequest(currentBaseRequest, { ...filterBase, dateRange: currentRange });
      const fetches = [makePostRequest("/ocel/object-table", currentRequest, {}, "Effizienz-KPI")];

      if (previousRange) {
        const previousRequest = createFilteredRequest(previousBaseRequest, { ...filterBase, dateRange: previousRange });
        fetches.push(makePostRequest("/ocel/object-table", previousRequest, {}, "Effizienz-KPI (Vorperiode)"));
      } else {
        fetches.push(Promise.resolve(null));
      }

      const [currentResponse, previousResponse] = await Promise.all(fetches);
      if (seq !== fetchSeqRef.current) return;
      setKpiData(currentResponse);
      setPreviousKpiData(previousResponse);
      setResolvedRange(currentRange);
      setResolvedPreviousRange(previousRange);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err?.message || "Failed to load efficiency KPI");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [
    activeFile, objectType, numerator, denominator, tooltipAttributes, periodPreset, customStart, customEnd,
    comparisonEnabled, hasLocalFilters, localFilters, globalFilterState,
  ]);

  // Fire after Apply commits new config + filters
  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApply]);

  // Auto-reload when filters or active file changes (after first load)
  useEffect(() => {
    if (hasFetched) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  useAutoLoad(fetchData, [activeFile, currentFilters], kpiData);

  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
  }, [onRefresh, fetchData]);

  // A non-"all" period is a local override in its own right, but it no longer
  // lives inside localFilters.dateRange (see fetchData) — reflect it in the
  // badge here instead of writing it back into hook state.
  const periodIsLocalOverride = periodPreset !== ALL_TIME_PRESET.key;
  const displayHasLocalFilters = hasLocalFilters || periodIsLocalOverride;
  const displayLocalFilterCount = localFilterCount + (periodIsLocalOverride ? 1 : 0);

  // ── Header actions ─────────────────────────────────────────────────────────
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {displayHasLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={displayHasLocalFilters}
            localFilterCount={displayLocalFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, displayHasLocalFilters, displayLocalFilterCount, clearLocalFilters]
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  useEffect(() => {
    if (onTitleChange) onTitleChange(componentTitle || "Effizienz-KPI");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayHasLocalFilters, componentTitle]);

  // ── Derived display values ─────────────────────────────────────────────────
  const formulaResult = useMemo(
    () => computeFormulaValue({ numerator, denominator, numericSums: kpiData?.aggregations?.numeric_sums }),
    [numerator, denominator, kpiData]
  );

  // "Gesamter Zeitraum" has no meaningful Vorperiode at all — treat comparison
  // as off regardless of the raw stored flag, so a tile switched to this
  // preset (without the user separately unchecking the box, which the
  // Settings UI already disables/greys out) doesn't show a dangling "Kein
  // Vergleichswert verfügbar" placeholder for a comparison that was never
  // possible in the first place.
  const effectiveComparisonEnabled = comparisonEnabled && periodPreset !== ALL_TIME_PRESET.key;

  const previousFormulaResult = useMemo(
    () => (effectiveComparisonEnabled && previousKpiData
      ? computeFormulaValue({ numerator, denominator, numericSums: previousKpiData?.aggregations?.numeric_sums })
      : null),
    [effectiveComparisonEnabled, numerator, denominator, previousKpiData]
  );

  const ampelColor = useMemo(() => {
    if (!ampelEnabled || formulaResult.error) return null;
    return resolveAmpelColor(formulaResult.value, {
      lower: ampelLower,
      upper: ampelUpper,
      colorRed: ampelColorRed,
      colorYellow: ampelColorYellow,
      colorGreen: ampelColorGreen,
    });
  }, [ampelEnabled, formulaResult, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen]);

  // When the active Ampel zone is red/green, match the trend pill's own
  // bg-x-50/text-x-700 palette exactly, regardless of which hex the user
  // configured for that zone — keeps the two indicators visually consistent.
  // Yellow (or any custom color that isn't literally the configured red/green)
  // has no trend-pill equivalent, so it keeps the dynamic hex-tint fallback.
  const ampelBadgeClass =
    ampelColor === ampelColorGreen ? TREND_BADGE_CLASSES.positive
    : ampelColor === ampelColorRed ? TREND_BADGE_CLASSES.negative
    : null;

  // Tooltip A's object table (see below) — kpiData.rows only carries the full
  // row set when tooltipAttributes is configured (fetchData bumps limit/attrs
  // accordingly); with no configured attributes it stays the pre-existing
  // limit:1 fetch and no table is shown at all.
  const tooltipObjectRows = kpiData?.rows ?? [];
  const showObjectTooltipTable = tooltipAttributes.length > 0;
  const tooltipTotalObjects = kpiData?.aggregations?.total_objects ?? 0;

  const dataBasisText = useMemo(() => {
    const formatRows = (rows) =>
      (rows || [])
        .filter((r) => r.attribute)
        .map((r, i) => (i === 0 ? r.attribute : ` ${r.operator === "-" ? "-" : "+"} ${r.attribute}`))
        .join("");
    const numeratorLabel = formatRows(numerator);
    const denominatorLabel = formatRows(denominator);
    if (!numeratorLabel || !denominatorLabel) return null;
    return { numeratorLabel, denominatorLabel };
  }, [numerator, denominator]);

  const displayTitle = componentTitle || "Effizienz-KPI";

  const hasFormulaConfigured =
    collectFormulaAttributes(numerator, denominator).length > 0;

  const periodLabel = showPeriodLabel && resolvedRange
    ? formatRangeLabel(resolvedRange)
    : null;

  const previousPeriodLabel = resolvedPreviousRange
    ? formatRangeLabel(resolvedPreviousRange)
    : null;

  const comparisonCaption = previousPeriodLabel
    ? formatComparisonCaption(periodPreset, previousPeriodLabel)
    : null;

  // Trendindikator = absolute Differenz in Prozentpunkten (aktueller Wert -
  // Vorperiodenwert) — beide formulaResult-Werte sind bereits 0-100-Prozentwerte.
  const trend = useMemo(() => {
    if (!effectiveComparisonEnabled || formulaResult.error || !previousFormulaResult || previousFormulaResult.error) {
      return null;
    }
    const diff = formulaResult.value - previousFormulaResult.value;
    const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
    const improved = trendDirection === "higherIsBetter" ? diff > 0 : diff < 0;
    const worsened = trendDirection === "higherIsBetter" ? diff < 0 : diff > 0;
    const color = diff === 0 ? "neutral" : improved ? "positive" : worsened ? "negative" : "neutral";
    return { diff, direction, color };
  }, [effectiveComparisonEnabled, formulaResult, previousFormulaResult, trendDirection]);

  return (
    <BaseTile loading={loading} error={error} minHeight="sm" padding="sm">
      {/* Placeholder when not yet configured */}
      {(!hasFetched || !hasFormulaConfigured) && !loading && !error && (
        <TileStatusContent
          status="info"
          title="Effizienz-KPI konfigurieren"
          message="Klicken Sie auf das Zahnrad, um Objekttyp und Formel zu konfigurieren."
          icon={
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      )}

      {/* KPI display — same grid structure and text palette regardless of
          Ampel/Vergleich combination ("kein doppeltes Design"). Ampel only
          tints a small badge directly around the value itself, like the
          trend pill's own bg-x-50/text-x-700 treatment, instead of the
          whole tile content block. */}
      {hasFetched && hasFormulaConfigured && !loading && !error && kpiData && (
        <TileContent align="stretch" justify="start">
          <div className="w-full flex flex-col items-center gap-1 py-1">
            <TooltipProvider>
              <div className="text-sm font-medium text-gray-700">
                {displayTitle}
              </div>

              {formulaResult.error ? (
                <div className="text-sm font-medium text-gray-500">
                  {ERROR_MESSAGES[formulaResult.error]}
                </div>
              ) : (
                // Without a comparison, column 2 is always empty — a two-column
                // grid still reserves a column-gap next to it, which would offset
                // the visible content from the (correctly centered) label above.
                // Single-column + justify-items-center avoids that (see the same
                // fix in AggregationKPI.jsx).
                <KPIValueRow>
                  <KPIValueColumn>
                    <HoverInfoCard
                      variant={showObjectTooltipTable ? "card" : "plain"}
                      trigger={
                        <KPIValueSlot className="cursor-default">
                          <KPIBigValue
                            className={ampelColor ? `rounded-lg px-3 py-0.5 ${ampelBadgeClass || ""}` : undefined}
                            style={ampelColor && !ampelBadgeClass ? { backgroundColor: `${ampelColor}26`, color: ampelColor } : undefined}
                          >
                            {formatPercent(formulaResult.value)}
                          </KPIBigValue>
                        </KPIValueSlot>
                      }
                    >
                      <div className={showObjectTooltipTable ? "px-3.5 py-2 text-[0.72rem] font-medium text-[#0f172a]" : undefined}>
                        {dataBasisText ? (
                          // Math-style fraction: the divider bar is a flex child sized to
                          // w-full of this column, which itself auto-sizes to the wider of
                          // numerator/denominator — so the bar always spans the longer line,
                          // exactly like a real formula, not just the numerator's own width.
                          <div className="flex flex-col items-center text-center gap-0.5 py-0.5">
                            <span>{dataBasisText.numeratorLabel}</span>
                            <span className={showObjectTooltipTable ? "w-full h-px bg-[#e2e8f0]" : "w-full h-px bg-white/40"} />
                            <span>{dataBasisText.denominatorLabel}</span>
                          </div>
                        ) : (
                          "Formel"
                        )}
                      </div>
                      {showObjectTooltipTable && (
                        <ObjectAttributeTable
                          attributes={tooltipAttributes}
                          rows={tooltipObjectRows}
                          totalCount={tooltipTotalObjects}
                          formatCellValue={formatCellValue}
                        />
                      )}
                    </HoverInfoCard>
                    {periodLabel && <KPICaption>{periodLabel}</KPICaption>}
                  </KPIValueColumn>

                  {effectiveComparisonEnabled && (
                    <KPIValueColumn>
                      {trend ? (
                        <HoverInfoCard
                          variant="plain"
                          trigger={
                            <KPITrendSlot className="cursor-default">
                              <KPITrendBadge trend={trend}>{formatTrendPP(trend)}</KPITrendBadge>
                            </KPITrendSlot>
                          }
                        >
                          <div>Aktuell: {formatPercent(formulaResult.value)}</div>
                          <div>Vorperiode: {formatPercent(previousFormulaResult.value)}</div>
                          {previousPeriodLabel && <div>{previousPeriodLabel}</div>}
                        </HoverInfoCard>
                      ) : (
                        <KPITrendSlot>
                          <KPICaption>Kein Vergleichswert verfügbar</KPICaption>
                        </KPITrendSlot>
                      )}

                      {trend && comparisonCaption && <KPICaption>{comparisonCaption}</KPICaption>}
                    </KPIValueColumn>
                  )}
                </KPIValueRow>
              )}
            </TooltipProvider>
          </div>
        </TileContent>
      )}

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Effizienz-KPI Settings"
        getInitialValues={() => ({
          componentTitle, objectType, numerator, denominator, tooltipAttributes,
          ampelEnabled, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen,
          periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
        })}
        onApply={(values) => {
          setComponentTitle(values.componentTitle);
          setObjectType(values.objectType);
          setNumerator(values.numerator);
          setDenominator(values.denominator);
          setTooltipAttributes(values.tooltipAttributes ?? []);
          setAmpelEnabled(values.ampelEnabled);
          setAmpelLower(values.ampelLower);
          setAmpelUpper(values.ampelUpper);
          setAmpelColorRed(values.ampelColorRed);
          setAmpelColorYellow(values.ampelColorYellow);
          setAmpelColorGreen(values.ampelColorGreen);
          setPeriodPreset(values.periodPreset);
          setCustomStart(values.customStart);
          setCustomEnd(values.customEnd);
          setShowPeriodLabel(values.showPeriodLabel);
          setComparisonEnabled(values.comparisonEnabled);
          setTrendDirection(values.trendDirection);
          if (onTitleChange && values.componentTitle) {
            onTitleChange(values.componentTitle);
          }
          applyDraftFilters();
          setPendingApply(true);
        }}
        applyButtonText="Apply & Load"
        isApplyDisabled={false}
      >
        {({ values, setField }) => (
          <EfficiencyKPISettings
            componentTitle={values.componentTitle}
            onTitleChange={(v) => setField("componentTitle", v)}
            objectType={values.objectType}
            onObjectTypeChange={(v) => setField("objectType", v)}
            numerator={values.numerator}
            onNumeratorChange={(v) => setField("numerator", v)}
            denominator={values.denominator}
            onDenominatorChange={(v) => setField("denominator", v)}
            tooltipAttributes={values.tooltipAttributes ?? []}
            onTooltipAttributesChange={(v) => setField("tooltipAttributes", v)}
            ampelEnabled={values.ampelEnabled}
            ampelLower={values.ampelLower}
            ampelUpper={values.ampelUpper}
            ampelColorRed={values.ampelColorRed}
            ampelColorYellow={values.ampelColorYellow}
            ampelColorGreen={values.ampelColorGreen}
            onAmpelEnabledChange={(v) => setField("ampelEnabled", v)}
            onAmpelLowerChange={(v) => setField("ampelLower", v)}
            onAmpelUpperChange={(v) => setField("ampelUpper", v)}
            onAmpelColorRedChange={(v) => setField("ampelColorRed", v)}
            onAmpelColorYellowChange={(v) => setField("ampelColorYellow", v)}
            onAmpelColorGreenChange={(v) => setField("ampelColorGreen", v)}
            availableObjectTypes={availableObjectTypes}
            availableActivities={availableActivities}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            activeFile={activeFile}
            loading={loading}
            periodPreset={values.periodPreset}
            onPeriodPresetChange={(v) => setField("periodPreset", v)}
            customStart={values.customStart}
            onCustomStartChange={(v) => setField("customStart", v)}
            customEnd={values.customEnd}
            onCustomEndChange={(v) => setField("customEnd", v)}
            showPeriodLabel={values.showPeriodLabel}
            onShowPeriodLabelChange={(v) => setField("showPeriodLabel", v)}
            comparisonEnabled={values.comparisonEnabled}
            onComparisonEnabledChange={(v) => setField("comparisonEnabled", v)}
            trendDirection={values.trendDirection}
            onTrendDirectionChange={(v) => setField("trendDirection", v)}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
};

export default EfficiencyKPI;
